import React, { useState, useEffect, useMemo } from "react";
import { 
  TableColumnsType, 
  Tag, 
  notification, 
  Table, 
  Typography, 
  Space, 
  Row, 
  Col, 
  Dropdown, 
  Button, 
  Tooltip, 
  Spin, 
  Empty
} from "antd";
import { FinetuningService } from "../../services/FinetuningService";
import { usePermissions } from "../../PermissionsProvider";
import { Model } from "../../model_gen/model";
import { PoweroffOutlined, LinkOutlined, MoreOutlined, PlusOutlined } from "@ant-design/icons";
import { ModelService } from "../../services/ModelService";
import { Observable } from "rxjs";
import { Link, useNavigate } from "react-router-dom";
import { PrimaryButton } from "../shared/button/Buttons";
import moment from "moment";

const Models: React.FC = () => {
  const { apiKey } = usePermissions();
  const [modelData, setModelData] = useState<Model[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const navigate = useNavigate();

  const styles = {
    contentCard: {
      background: "#fff",
      padding: "24px",
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      border: "1px solid #f0f0f0",
    },
    tableContainer: {
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    },
    detailsHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "24px",
    },
    expandedTable: {
      margin: 0,
      padding: "12px 48px",
    }
  };

  // Add minimal CSS
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .models-container {
        background-color: #f5f7fa;
      }
      .interactive-row {
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const fetchData = () => {
    FinetuningService.getFinetunedModels(setIsLoading, apiKey).subscribe(
      (data: Model[]) => {
        setModelData(data);
      },
      (error: Error) => {
        console.error("Error fetching model data:", error);
        notification.error({
          message: "Error",
          description: "Failed to load models",
        });
      }
    );
  };

  useEffect(() => {
    fetchData();
  }, [apiKey]);

  const groupedData = useMemo(() => {
    const groups = modelData.reduce(
      (acc: { [key: string]: Model[] }, model) => {
        if (!acc[model.model]) {
          acc[model.model] = [];
        }
        acc[model.model].push(model);
        return acc;
      },
      {}
    );

    return Object.entries(groups).map(([modelName, models]) => {
      // Ensure we have valid values for key components
      const groupId = models[0].intent_group_id || "no-group";

      return {
        key: `${modelName}-${groupId}`, // More stable composite key
        model: modelName,
        base_model: models.length === 1 ? models[0].base_model : undefined,
        group_name: models.length === 1 ? models[0].group_name : undefined,
        status: models.length === 1 ? models[0].status : undefined,
        date_added: models.length === 1 ? models[0].date_added : undefined,
        subModels: models.map((m) => ({
          ...m,
          key: m.id, // Should be unique
        })),
      };
    });
  }, [modelData]);

  const currentPageData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return groupedData.slice(startIndex, startIndex + pageSize);
  }, [currentPage, pageSize, groupedData]);

  const columns: TableColumnsType<any> = [
    {
      title: "Model Name",
      dataIndex: "model",
      key: "model",
      width: "25%",
      render: (text: string) => (
        <Typography.Text style={{ fontWeight: 500, color: "#1890ff" }}>
          {text}
        </Typography.Text>
      ),
    },
    {
      title: "Base Model",
      dataIndex: "base_model",
      key: "base_model",
      width: "20%",
      render: (text: string) => (
        <Typography.Text>
          {text || "-"}
        </Typography.Text>
      ),
    },
    {
      title: "Group Name",
      dataIndex: "group_name",
      key: "group_name",
      width: "20%",
      render: (text: string) => (
        <Typography.Text>
          {text || "-"}
        </Typography.Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "15%",
      render: (status: string | undefined) => {
        if (!status) return <Typography.Text type="secondary">-</Typography.Text>;

        let color = "default";
        switch (status.toLowerCase()) {
          case "active":
          case "enabled":
            color = "success";
            break;
          case "disabled":
            color = "error";
            break;
          case "pending":
            color = "processing";
            break;
          default:
            color = "default";
        }
        return <Tag color={color}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      title: "Date Created",
      dataIndex: "date_added",
      key: "date_added",
      width: "20%",
      render: (timestamp: number | undefined) => {
        if (!timestamp) return <Typography.Text type="secondary">-</Typography.Text>;
        const date = moment(timestamp);
        return (
          <Tooltip title={date.format("MMMM D, YYYY h:mm:ss A")}>
            <div>
              <div style={{ fontWeight: 500 }}>{date.format("MMM D, YYYY")}</div>
              <div style={{ color: "#8c8c8c", fontSize: "12px" }}>{date.format("h:mm A")}</div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: "5%",
      render: (_, record) => {
        if (record.subModels?.length > 1) return null;
        const model = record.subModels?.[0] || record;
        return (
          <Dropdown
            menu={{
              items: [
                {
                  key: "toggle",
                  label: model.status === "ENABLED" ? "Disable" : "Enable",
                  icon: <PoweroffOutlined />,
                  onClick: (e) => {
                    e.domEvent.stopPropagation();
                    handleModelStatusToggle(model);
                  },
                },
                {
                  key: "view",
                  label: "View Finetune Run",
                  icon: <LinkOutlined />,
                  disabled: true,
                  onClick: (e) => {
                    e.domEvent.stopPropagation();
                    // TODO: Implement finetune run link
                  },
                },
              ],
            }}
            trigger={["click"]}>
            <Button
              type="text"
              icon={<MoreOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        );
      },
    },
  ];

  const expandedColumns: TableColumnsType<any> = [
    {
      title: "Base Model",
      dataIndex: "base_model",
      key: "base_model",
      width: "25%",
      render: (text: string) => (
        <Typography.Text>
          {text || "-"}
        </Typography.Text>
      ),
    },
    {
      title: "Group Name",
      dataIndex: "group_name",
      key: "group_name",
      width: "25%",
      render: (text: string) => (
        <Typography.Text>
          {text || "-"}
        </Typography.Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "20%",
      render: (status: string) => (
        <Tag color={status === "ENABLED" ? "success" : "error"}>{status}</Tag>
      ),
    },
    {
      title: "Date Created",
      dataIndex: "date_added",
      key: "date_added",
      width: "25%",
      render: (timestamp: number) => {
        if (!timestamp) return <Typography.Text type="secondary">-</Typography.Text>;
        const date = moment(timestamp);
        return (
          <Tooltip title={date.format("MMMM D, YYYY h:mm:ss A")}>
            <div>
              <div style={{ fontWeight: 500 }}>{date.format("MMM D, YYYY")}</div>
              <div style={{ color: "#8c8c8c", fontSize: "12px" }}>{date.format("h:mm A")}</div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: "5%",
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "toggle",
                label: record.status === "ENABLED" ? "Disable" : "Enable",
                icon: <PoweroffOutlined />,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  handleModelStatusToggle(record);
                },
              },
              {
                key: "view",
                label: "View Finetune Run",
                icon: <LinkOutlined />,
                disabled: true,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  // TODO: Implement finetune run link
                },
              },
            ],
          }}
          trigger={["click"]}>
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  const handleModelStatusToggle = (model: Model) => {
    const newStatus = model.status === "ENABLED" ? "DISABLED" : "ENABLED";
    setIsLoading(true);
    let modelObservable: Observable<any>;
    if (newStatus === "DISABLED") {
      modelObservable = ModelService.disableModel(
        model.id,
        setIsLoading,
        apiKey
      );
    } else {
      modelObservable = ModelService.enableModel(
        model.id,
        setIsLoading,
        apiKey
      );
    }
    modelObservable.subscribe(
      () => {
        fetchData();
        notification.success({
          message: "Success",
          description: `Model ${
            newStatus === "ENABLED" ? "enabled" : "disabled"
          } successfully`,
        });
      },
      (error: Error) => {
        console.error("Error updating model status:", error);
        notification.error({
          message: "Error",
          description: "Failed to update model status",
        });
      }
    );
  };

  if (isLoading && modelData.length === 0) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "calc(100vh - 200px)" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto", minHeight: "calc(100vh - 64px)" }}>
      {/* Header */}
      <div style={{ marginBottom: "32px" }}>
        <Row align="middle">
          <Col>
            <Space direction="vertical" size={4}>
              <Typography.Text type="secondary">
                <Link to="/">Home</Link> / Finetuning / Models
              </Typography.Text>
              <Typography.Title level={2} style={{ margin: 0, fontWeight: 600 }}>
                Models
              </Typography.Title>
              <Typography.Text type="secondary" style={{ fontSize: "14px" }}>
                Manage your fine-tuned models
              </Typography.Text>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Main Content */}
      <div style={styles.contentCard}>
        <div style={styles.detailsHeader}>
          <Typography.Title level={5} style={{ margin: 0, fontWeight: 600 }}>
            Models
          </Typography.Title>
          <PrimaryButton
            title="New Model"
            icon={<PlusOutlined />}
            onClick={() => navigate("/models/finetuning/new")}
          />
        </div>
        <div style={styles.tableContainer}>
          <Table
            columns={columns}
            dataSource={currentPageData}
            rowKey="key"
            loading={isLoading && modelData.length > 0}
            rowClassName={() => "interactive-row"}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: groupedData.length,
              showSizeChanger: false,
              showTotal: (total) => `Total ${total} items`,
              onChange: (page, size) => {
                setCurrentPage(page);
                if (size) setPageSize(size);
              }
            }}
            expandable={{
              expandedRowRender: (record) => (
                <div style={styles.expandedTable}>
                  <Table
                    columns={expandedColumns}
                    dataSource={record.subModels}
                    pagination={false}
                    rowKey="id"
                    rowClassName={() => "interactive-row"}
                  />
                </div>
              ),
              rowExpandable: (record) =>
                record?.subModels && record.subModels.length > 1,
            }}
            locale={{
              emptyText: (
                <Empty 
                  description={
                    <div>
                      <Typography.Text strong style={{ display: "block", marginBottom: "8px" }}>
                        No models found
                      </Typography.Text>
                      <Typography.Text type="secondary">
                        Create a new fine-tuned model by starting a finetune run.
                      </Typography.Text>
                    </div>
                  } 
                  image={Empty.PRESENTED_IMAGE_SIMPLE} 
                />
              )
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Models;
